import { Component } from '@theme/component';

/**
 * Class filter component - handles navigation pills that scroll to date/time groups
 *
 * @extends {Component}
 */
class ClassFilterComponent extends Component {
  /** @type {IntersectionObserver | null} */
  observer = null;

  /** @type {boolean} */
  isScrollingProgrammatically = false;

  /** @type {number | null} */
  intersectionDebounceTimer = null;

  /** @type {boolean} */
  isUpdatingDOM = false;

  /** @type {ResizeObserver | null} */
  resizeObserver = null;

  /** @type {number | null} */
  lastStickyHeight = null;

  /**
   * Update CSS variable with sticky container height
   */
  updateStickyHeight() {
    const container = this.closest('.class-filter__sticky-container');
    const height = container ? container.offsetHeight : this.offsetHeight || 0;

    document.documentElement.style.setProperty('--class-filter-sticky-height', `${height}px`);

    const heightChanged = this.lastStickyHeight !== height;
    this.lastStickyHeight = height;

    if (heightChanged || !this.observer) {
      this.setupScrollSpy();
    }
  }

  /**
   * Handle filter pill click - bound to instance for cleanup
   * @param {MouseEvent} event
   */
  handleFilterClick = async (event) => {
    const button = event.target.closest('.class-filter__pill');
    if (!button) return;

    event.preventDefault();

    const filterType = button.dataset.filterType;
    const filterValue = button.dataset.filterValue;
    const scrollTarget = button.dataset.scrollTarget;

    // Disable scroll spy during programmatic scrolling
    this.isScrollingProgrammatically = true;

    // Update active states
    this.updateActiveState(button, filterType);

    // Handle time group visibility based on date selection
    if (filterType === 'date') {
      this.updateTimeSlots(filterValue, button.dataset.times);
    } else if (filterType === 'time') {
      // Clear date selection when time pill is clicked
      this.clearDateSelection();
    }

    // Scroll to target
    if (filterValue !== 'all' && scrollTarget) {
      await this.scrollToTarget(scrollTarget);
    } else if (filterValue === 'all') {
      // Scroll to section container
      await this.scrollToSection();
    }

    // Re-enable scroll spy after scrolling completes
    setTimeout(() => {
      this.isScrollingProgrammatically = false;
    }, 600);
  };

  /**
   * Update time slots visibility based on selected date
   * @param {string} dateValue - The selected date value ('all' or date key)
   * @param {string} timesForDate - Comma-separated list of times for this date
   */
  updateTimeSlots(dateValue, timesForDate) {
    const timeGroup = this.querySelector('.class-filter__group--times');
    if (!timeGroup) return;

    const sectionId = this.dataset.sectionId;

    // Hide time group if "All Days" is selected
    if (dateValue === 'all') {
      timeGroup.style.display = 'none';
      requestAnimationFrame(() => this.updateStickyHeight());
      return;
    }

    // Show time group and filter time pills
    timeGroup.style.display = 'flex';

    const timePills = timeGroup.querySelectorAll('[data-filter-type="time"]');
    const allowedTimes = timesForDate ? timesForDate.split(',') : [];

    // Single loop: show/hide pills, update scroll targets, and reset active states
    timePills.forEach((pill) => {
      const timeValue = pill.dataset.filterValue;
      const isAllowed = allowedTimes.includes(timeValue);

      // Show or hide based on allowed times
      pill.style.display = isAllowed ? 'inline-flex' : 'none';

      // Update scroll target to include date
      if (isAllowed) {
        const timeHandle = this.toHandle(timeValue);
        pill.dataset.scrollTarget = `${sectionId}-time-${dateValue}--${timeHandle}`;
      } else {
        delete pill.dataset.scrollTarget;
      }

      // Reset active state
      pill.classList.remove('class-filter__pill--active');
      pill.setAttribute('aria-pressed', 'false');
    });

    // Update sticky height after DOM changes
    requestAnimationFrame(() => this.updateStickyHeight());
  }

  /**
   * Update active state for filter pills
   * @param {HTMLButtonElement} activeButton
   * @param {string} filterType
   */
  updateActiveState(activeButton, filterType) {
    // Find all pills of the same type
    const pills = this.querySelectorAll(`[data-filter-type="${filterType}"]`);

    // Remove active state from all pills
    pills.forEach((pill) => {
      pill.classList.remove('class-filter__pill--active');
      pill.setAttribute('aria-pressed', 'false');
    });

    // Add active state to clicked pill
    activeButton.classList.add('class-filter__pill--active');
    activeButton.setAttribute('aria-pressed', 'true');
  }

  /**
   * Clear date selection and show all time pills
   */
  clearDateSelection() {
    const datePills = this.querySelectorAll('[data-filter-type="date"]');
    datePills.forEach((pill) => {
      pill.classList.remove('class-filter__pill--active');
      pill.setAttribute('aria-pressed', 'false');
    });
  }

  /**
   * Convert string to handle format (lowercase, hyphenated)
   * @param {string} str - String to convert
   * @returns {string} Handleized string
   */
  toHandle(str) {
    if (!str) return '';

    let normalized = `${str}`.trim();

    if (normalized === '') return '';

    if (typeof normalized.normalize === 'function') {
      normalized = normalized.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    }

    normalized = normalized.replace(/&/g, 'and').replace(/['’]/g, '');
    normalized = normalized.toLowerCase();

    return normalized
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Setup scroll spy using IntersectionObserver
   */
  setupScrollSpy() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const dateGroups = Array.from(document.querySelectorAll(`[id^="${sectionId}-date-"]`));
    const timeGroups = Array.from(document.querySelectorAll(`[id^="${sectionId}-time-"]`));
    const targets = [...dateGroups, ...timeGroups];

    if (!targets.length) {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      return;
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    // Calculate root margin to account for sticky header
    // Top: offset for sticky header
    // Bottom: -30% allows earlier detection for smoother pill transitions
    const headerOffset = Number.isFinite(this.lastStickyHeight) ? this.lastStickyHeight : this.offsetHeight;
    const offset = Number.isFinite(headerOffset) ? headerOffset : 0;
    const rootMargin = `-${offset}px 0px -30% 0px`;

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        rootMargin,
        threshold: [0, 0.1, 0.5, 1],
      }
    );

    targets.forEach((group) => this.observer.observe(group));
  }

  /**
   * Handle intersection observer callback
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    // Don't update during programmatic scrolling or DOM updates
    if (this.isScrollingProgrammatically || this.isUpdatingDOM) return;

    // Debounce rapid intersection events
    if (this.intersectionDebounceTimer) {
      clearTimeout(this.intersectionDebounceTimer);
    }

    this.intersectionDebounceTimer = setTimeout(() => {
      // Find the most visible entry
      let mostVisible = null;
      let maxRatio = 0;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisible = entry.target;
        }
      });

      if (mostVisible) {
        this.updateActiveFromScroll(mostVisible.id);
      }

      this.intersectionDebounceTimer = null;
    }, 100);
  }

  /**
   * Update active pill based on visible section
   * @param {string} visibleId - ID of the visible section
   */
  updateActiveFromScroll(visibleId) {
    // Prevent race conditions during DOM updates
    this.isUpdatingDOM = true;

    const sectionId = this.dataset.sectionId;

    // Check if it's a time group (format: sectionId-time-date--time)
    if (visibleId.includes('-time-')) {
      const remainder = visibleId.replace(`${sectionId}-time-`, '');
      const [dateValue] = remainder.split('--');

      if (dateValue) {
        // Find and activate the date pill
        const datePill = this.querySelector(`[data-filter-type="date"][data-filter-value="${dateValue}"]`);
        if (datePill) {
          // Update time slots for this date (which will set scroll targets correctly)
          this.updateTimeSlots(dateValue, datePill.dataset.times);

          // Activate the date pill
          const allDatePills = this.querySelectorAll('[data-filter-type="date"]');
          allDatePills.forEach((pill) => {
            pill.classList.remove('class-filter__pill--active');
            pill.setAttribute('aria-pressed', 'false');
          });
          datePill.classList.add('class-filter__pill--active');
          datePill.setAttribute('aria-pressed', 'true');
        }
      }

      // Find and activate the time pill
      const timePill = this.querySelector(`[data-filter-type="time"][data-scroll-target="${visibleId}"]`);
      if (timePill) {
        const allTimePills = this.querySelectorAll('[data-filter-type="time"]');
        allTimePills.forEach((pill) => {
          pill.classList.remove('class-filter__pill--active');
          pill.setAttribute('aria-pressed', 'false');
        });

        timePill.classList.add('class-filter__pill--active');
        timePill.setAttribute('aria-pressed', 'true');

        // Show time group if hidden
        const timeGroup = this.querySelector('.class-filter__group--times');
        if (timeGroup) {
          timeGroup.style.display = 'flex';
        }
      }
    }
    // Check if it's a date group
    else if (visibleId.includes('-date-')) {
      const dateValue = visibleId.replace(`${sectionId}-date-`, '');
      const datePill = this.querySelector(`[data-filter-type="date"][data-scroll-target="${visibleId}"]`);

      if (datePill) {
        // Update date pill active state
        const allDatePills = this.querySelectorAll('[data-filter-type="date"]');
        allDatePills.forEach((pill) => {
          pill.classList.remove('class-filter__pill--active');
          pill.setAttribute('aria-pressed', 'false');
        });

        datePill.classList.add('class-filter__pill--active');
        datePill.setAttribute('aria-pressed', 'true');

        // Update time slots for this date
        this.updateTimeSlots(dateValue, datePill.dataset.times);
      }
    }

    // Reset DOM update flag after updates complete
    requestAnimationFrame(() => {
      this.isUpdatingDOM = false;
    });
  }

  /**
   * Determine whether the user prefers reduced motion.
   * @returns {boolean}
   */
  prefersReducedMotion() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Scroll to the section container
   * @returns {Promise<void>}
   */
  async scrollToSection() {
    const sectionId = this.dataset.sectionId;
    const section = sectionId ? document.getElementById(sectionId) : null;
    const reduceMotion = this.prefersReducedMotion();

    if (section) {
      const offset = this.offsetHeight + 20;
      const targetPosition = section.getBoundingClientRect().top + window.pageYOffset - offset;

      if (reduceMotion) {
        window.scrollTo({ top: targetPosition });
      } else {
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }
    } else {
      // Fallback: scroll to top of page
      if (reduceMotion) {
        window.scrollTo({ top: 0 });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }

    if (!reduceMotion) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  /**
   * Smooth scroll to target element
   * @param {string} targetId
   * @returns {Promise<void>}
   */
  async scrollToTarget(targetId) {
    const target = document.getElementById(targetId);

    if (!target) {
      console.warn(`Target element #${targetId} not found`);
      return;
    }

    // Use the entire component's height for offset calculation
    const offset = this.offsetHeight + 20;
    const reduceMotion = this.prefersReducedMotion();

    // Calculate scroll position
    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - offset;

    // Smooth scroll
    if (reduceMotion) {
      window.scrollTo({ top: targetPosition });
    } else {
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });
    }

    // Wait for scroll to complete, then focus the target for accessibility
    if (!reduceMotion) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // Set focus to target for screen readers
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    // Remove tabindex after blur
    target.addEventListener(
      'blur',
      () => {
        target.removeAttribute('tabindex');
      },
      { once: true }
    );
  }

  /**
   * Called when component is connected to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    // Use event delegation - single listener on component
    this.addEventListener('click', this.handleFilterClick);

    // Setup scroll spy after a brief delay to ensure DOM is ready
    requestAnimationFrame(() => {
      this.updateStickyHeight();
    });

    // Observe container size changes
    const container = this.closest('.class-filter__sticky-container');
    if (container && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateStickyHeight();
      });
      this.resizeObserver.observe(container);
    }
  }

  /**
   * Called when component is disconnected from DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up event listener
    this.removeEventListener('click', this.handleFilterClick);

    // Clear debounce timer
    if (this.intersectionDebounceTimer) {
      clearTimeout(this.intersectionDebounceTimer);
      this.intersectionDebounceTimer = null;
    }

    // Disconnect ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Disconnect IntersectionObserver
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

customElements.define('class-filter-component', ClassFilterComponent);
